import { useEffect, useRef, useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Star } from "lucide-react";
import { loadGoogleMaps, getGoogleMapsKey, NAPLES_BOUNDS, NAPLES_CENTER } from "@/lib/googleMaps";

// Address input with a CUSTOM autocomplete dropdown. Supports:
//  - Google Places predictions (Naples-first)
//  - Saved aliases (matched by name substring) shown FIRST with a star.
export default function PlaceAutocomplete({
  value, onChange, onPlace, testId, placeholder, className, aliases,
}) {
  const inputRef = useRef(null);
  const serviceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [query, setQuery] = useState(value || "");
  const [predictions, setPredictions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  // Flag flipped by `selectAlias` / `selectPrediction` right before they
  // update `query` with the picked address. The prediction-fetching effect
  // checks this and skips exactly ONE run — otherwise the address we just
  // committed goes right back into Google's autocomplete API, the list
  // reopens, and the dispatcher has to click a second time.
  const justSelectedRef = useRef(false);

  const hasKey = !!getGoogleMapsKey();

  // Filter aliases by query
  const aliasMatches = useMemo(() => {
    if (!aliases || aliases.length === 0) return [];
    const q = (query || "").trim().toLowerCase();
    if (!q) return [];
    return aliases
      .filter((a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.address || "").toLowerCase().includes(q)
      )
      .slice(0, 5);
  }, [aliases, query]);

  // Combined list: aliases first, then predictions
  const combined = useMemo(() => {
    const a = aliasMatches.map((al) => ({ kind: "alias", data: al, key: `alias-${al.id}` }));
    const p = predictions.map((pr) => ({ kind: "place", data: pr, key: pr.place_id }));
    return [...a, ...p];
  }, [aliasMatches, predictions]);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    if (!hasKey) return;
    let mounted = true;
    loadGoogleMaps()
      .then((g) => {
        if (!mounted) return;
        serviceRef.current = new g.maps.places.AutocompleteService();
        sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();
      })
      .catch((err) => console.error("PlaceAutocomplete load failed:", err));
    return () => { mounted = false; };
  }, [hasKey]);

  useEffect(() => {
    if (!hasKey) return;
    // Skip the fetch once, right after a selection, to keep the list closed.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      setPredictions([]);
      return;
    }
    if (!query || query.length < 2 || !serviceRef.current) {
      setPredictions([]);
      // still open list if aliases match
      if (aliasMatches.length > 0) setShowList(true);
      return;
    }
    const t = setTimeout(() => {
      const g = window.google;
      const bounds = new g.maps.LatLngBounds(
        { lat: NAPLES_BOUNDS.south, lng: NAPLES_BOUNDS.west },
        { lat: NAPLES_BOUNDS.north, lng: NAPLES_BOUNDS.east },
      );
      serviceRef.current.getPlacePredictions(
        {
          input: query,
          locationBias: bounds,
          origin: new g.maps.LatLng(NAPLES_CENTER.lat, NAPLES_CENTER.lng),
          componentRestrictions: { country: "us" },
          sessionToken: sessionTokenRef.current,
        },
        (preds, status) => {
          if (status === g.maps.places.PlacesServiceStatus.OK && preds && preds.length) {
            const rank = (p) => {
              const sec = (p.structured_formatting?.secondary_text || "").toLowerCase();
              const desc = (p.description || "").toLowerCase();
              if (sec.includes("naples") || desc.includes("naples, fl")) return 0;
              if (sec.includes("marco island") || sec.includes("bonita") || sec.includes("estero")) return 1;
              return 2;
            };
            const sorted = [...preds].sort((a, b) => rank(a) - rank(b));
            setPredictions(sorted.slice(0, 6));
            setShowList(true);
            setActiveIdx(-1);
          } else {
            setPredictions([]);
          }
        },
      );
    }, 220);
    return () => clearTimeout(t);
  }, [query, hasKey, aliasMatches.length]);

  const selectAlias = async (al) => {
    justSelectedRef.current = true;
    setShowList(false);
    setActiveIdx(-1);
    setPredictions([]);
    setQuery(al.address);
    inputRef.current?.blur();
    // Happy path: alias already has coords captured at creation time.
    if (al.lat != null && al.lng != null) {
      onPlace?.({
        address: al.address,
        lat: al.lat,
        lng: al.lng,
        place_id: al.place_id || null,
      });
      return;
    }
    // Legacy aliases (RSW Airport, Casa B, etc.) were saved without lat/lng.
    // Resolving them client-side here means the trip's pickup/dropoff coords
    // get overwritten with REAL coords when the dispatcher edits an existing
    // trip and swaps in a favourite — otherwise the map keeps the old pin
    // under the new label (field bug reported 2026-09).
    setLoading(true);
    try {
      const g = window.google;
      if (g?.maps?.importLibrary) {
        if (al.place_id) {
          try {
            const { Place } = await g.maps.importLibrary("places");
            const place = new Place({ id: al.place_id });
            await place.fetchFields({ fields: ["formattedAddress", "location"] });
            const loc = place.location;
            const rl = (loc, k) => (loc == null ? null : typeof loc[k] === "function" ? loc[k]() : loc[k]);
            const lat = rl(loc, "lat");
            const lng = rl(loc, "lng");
            if (lat != null && lng != null) {
              onPlace?.({ address: place.formattedAddress || al.address, lat, lng, place_id: al.place_id });
              return;
            }
          } catch (err) {
            console.warn("selectAlias: place fetch failed, falling back to geocoder", err);
          }
        }
        // No place_id, or place fetch failed → free-text geocode by address.
        try {
          const geocoder = new g.maps.Geocoder();
          const res = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: al.address }, (results, status) => {
              if (status === "OK" && results?.[0]) resolve(results[0]);
              else reject(new Error(`geocode status=${status}`));
            });
          });
          const loc = res.geometry?.location;
          const rl = (loc, k) => (loc == null ? null : typeof loc[k] === "function" ? loc[k]() : loc[k]);
          const lat = rl(loc, "lat");
          const lng = rl(loc, "lng");
          if (lat != null && lng != null) {
            onPlace?.({ address: al.address, lat, lng, place_id: res.place_id || al.place_id || null });
            return;
          }
        } catch (err) {
          console.warn("selectAlias: geocode failed", err);
        }
      }
      // Absolute fallback: propagate null coords (behaviour before fix).
      // TripFormDialog will then keep the previous trip's coords via the
      // `?? f.pickup_lat` guard on the setForm call.
      onPlace?.({ address: al.address, lat: null, lng: null, place_id: al.place_id || null });
    } finally {
      setLoading(false);
    }
  };

  const selectPrediction = async (pred) => {
    justSelectedRef.current = true;
    setShowList(false);
    setActiveIdx(-1);
    setPredictions([]);
    setLoading(true);
    const g = window.google;
    try {
      const { Place } = await g.maps.importLibrary("places");
      const place = new Place({ id: pred.place_id });
      await place.fetchFields({ fields: ["formattedAddress", "location", "displayName"] });
      const address = place.formattedAddress || pred.description || "";
      const loc = place.location;
      // Google Maps SDK: location fields are sometimes callable (LatLng), sometimes plain numbers
      const readLat = (loc) => (loc == null ? null : typeof loc.lat === "function" ? loc.lat() : loc.lat);
      const readLng = (loc) => (loc == null ? null : typeof loc.lng === "function" ? loc.lng() : loc.lng);
      const lat = readLat(loc);
      const lng = readLng(loc);
      onPlace?.({ address, lat, lng, place_id: pred.place_id });
      setQuery(address);
    } catch (err) {
      console.error("Place.fetchFields failed:", err);
      onPlace?.({ address: pred.description || "", lat: null, lng: null, place_id: pred.place_id || null });
      setQuery(pred.description || "");
    } finally {
      setLoading(false);
      try {
        sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();
      } catch (err) {
        console.warn("PlaceAutocomplete: session token renewal failed:", err);
      }
      inputRef.current?.blur();
    }
  };

  const selectItem = (item) => {
    if (item.kind === "alias") selectAlias(item.data);
    else selectPrediction(item.data);
  };

  const handleKey = (e) => {
    if (!showList || combined.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % combined.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? combined.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectItem(combined[activeIdx]);
    } else if (e.key === "Escape") {
      setShowList(false);
      setActiveIdx(-1);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          onChange?.(v);
        }}
        onKeyDown={handleKey}
        onFocus={() => { if (combined.length) setShowList(true); }}
        onBlur={() => {
          closeTimerRef.current = setTimeout(() => setShowList(false), 180);
        }}
        placeholder={placeholder || (hasKey ? "Empieza a escribir una dirección…" : "Escribe la dirección")}
        data-testid={testId}
        autoComplete="off"
        className={className}
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground">
          …
        </div>
      )}
      {showList && combined.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-[100] bg-card border border-border rounded-md shadow-2xl max-h-64 overflow-auto"
          data-testid={`${testId}-suggestions`}
          onMouseDown={(e) => e.preventDefault()}
        >
          {combined.map((item, i) => {
            const isAlias = item.kind === "alias";
            const d = item.data;
            const active = i === activeIdx;
            return (
              <button
                type="button"
                key={item.key}
                onMouseDown={(e) => { e.preventDefault(); selectItem(item); clearTimeout(closeTimerRef.current); }}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`${testId}-suggestion-${i}`}
                className={`w-full text-left px-3 py-2.5 border-b border-border/50 last:border-b-0 flex items-start gap-2 transition-colors ${
                  active ? "bg-primary/15" : "hover:bg-secondary/40"
                } ${isAlias ? "bg-primary/5" : ""}`}
              >
                {isAlias ? (
                  <Star className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5 fill-primary" />
                ) : (
                  <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  {isAlias ? (
                    <>
                      <div className="text-sm font-bold text-white truncate">{d.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{d.address}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-white truncate">
                        {d.structured_formatting?.main_text || d.description}
                      </div>
                      {d.structured_formatting?.secondary_text && (
                        <div className="text-xs text-muted-foreground truncate">
                          {d.structured_formatting.secondary_text}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </button>
            );
          })}
          <div className="px-3 py-1 text-[10px] font-mono text-muted-foreground text-right border-t border-border/50">
            {aliasMatches.length > 0 && (
              <span className="mr-2"><Star className="w-2.5 h-2.5 inline text-primary" /> guardadas</span>
            )}
            powered by Google
          </div>
        </div>
      )}
    </div>
  );
}
